"use client";

import { partnerHrefs } from "@/lib/usePartnerCode";
import { useActivePartnerCode } from "@/lib/useActivePartnerCode";
import Link from "@/components/LocaleLink";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { ArrowRight, CalendarClock, Heart, Lock, Mail, MessageCircle } from "lucide-react";
import { SITE, mailLink, whatsappLink, telegramLink, bookingLink } from "@/lib/config";
import { BrandLogo } from "@/components/BrandLogo";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { isDockSuppressed } from "@/components/landing/ConversionDock";
import { reopenConsent } from "@/lib/consent";

/* Social glyphs in brand colours, borderless on the dark footer surface. */
export function InstagramBrandIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <defs>
        <radialGradient id="footer-ig-grad" cx="30%" cy="105%" r="115%">
          <stop offset="0%" stopColor="#FEDA75" />
          <stop offset="28%" stopColor="#FA7E1E" />
          <stop offset="50%" stopColor="#D62976" />
          <stop offset="72%" stopColor="#962FBF" />
          <stop offset="100%" stopColor="#4F5BD5" />
        </radialGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5.7" fill="url(#footer-ig-grad)" />
      <circle cx="12" cy="12" r="4.25" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="17.4" cy="6.6" r="1.2" fill="#fff" />
    </svg>
  );
}
export function FacebookBrandIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" fill="#1877F2" />
      <path
        d="M14.72 12.62h-1.9V19h-2.66v-6.38H8.8v-2.26h1.36V8.9c0-1.15.55-2.95 2.95-2.95l2.17.01v2.42h-1.57c-.26 0-.62.13-.62.68v1.3h2.23l-.6 2.26z"
        fill="#fff"
      />
    </svg>
  );
}
export function PinterestBrandIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="11" fill="#FFFFFF" />
      <path
        d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"
        fill="#E60023"
      />
    </svg>
  );
}
export function TiktokBrandIcon({ className }: { className?: string }) {
  const note =
    "M16.6 5.8a4.7 4.7 0 0 1-2.7-2.6h-2.6v11.4c0 1.4-1.1 2.5-2.5 2.5s-2.5-1.1-2.5-2.5 1.1-2.5 2.5-2.5c.3 0 .5 0 .8.1V9.5c-.3 0-.5-.1-.8-.1a5.4 5.4 0 1 0 5.4 5.4V9.4c1.1.7 2.4 1.1 3.7 1.1V8a4.7 4.7 0 0 1-1.3-.2z";
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d={note} fill="#25F4EE" transform="translate(-1 1)" />
      <path d={note} fill="#FE2C55" transform="translate(1 -0.7)" />
      <path d={note} fill="#FFFFFF" />
    </svg>
  );
}
function WhatsAppBrandIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10.5" fill="#25D366" />
      <path
        d="M12.02 5.4a6.39 6.39 0 0 0-5.49 9.65l-.72 2.64 2.7-.71a6.38 6.38 0 1 0 3.51-11.58Zm0 11.68h-.01a5.31 5.31 0 0 1-2.71-.74l-.2-.12-1.6.42.43-1.56-.13-.2A5.3 5.3 0 1 1 12.02 17.08Z"
        fill="#fff"
      />
      <path
        d="M15.03 13.22c-.17-.09-1.03-.51-1.19-.57-.16-.06-.28-.09-.4.09-.12.17-.46.57-.56.69-.1.12-.21.13-.38.04-.17-.09-.73-.27-1.39-.86-.51-.46-.86-1.03-.96-1.2-.1-.17-.01-.27.08-.36.08-.08.17-.21.26-.31.09-.1.12-.17.17-.29.06-.12.03-.22-.01-.31-.04-.09-.4-.96-.55-1.32-.14-.35-.29-.3-.4-.31h-.34c-.12 0-.31.04-.47.22-.16.17-.62.6-.62 1.46 0 .86.63 1.7.72 1.82.09.12 1.24 1.9 3 2.66.42.18.75.29 1 .37.42.13.81.11 1.11.07.34-.05 1.03-.42 1.18-.83.15-.41.15-.75.1-.83-.04-.08-.16-.13-.34-.22Z"
        fill="#fff"
      />
    </svg>
  );
}
function TelegramBrandIcon({ className }: { className?: string }) {
  // Telegram's app-icon disc with the white plane, scaled to the same 10.5
  // radius as the WhatsApp disc beside it.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10.5" fill="#229ED9" />
      <path
        transform="translate(12 12) scale(0.875) translate(-12 -12)"
        d="M16.906 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
        fill="#fff"
      />
    </svg>
  );
}

/* Payment logos in brand colours, rendered on light chips. */
export function VisaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 20" className={className} aria-hidden>
      <text
        x="0"
        y="16"
        fontFamily="Arial Black, system-ui, sans-serif"
        fontStyle="italic"
        fontSize="18"
        fontWeight="900"
        letterSpacing="0.8"
        fill="#1A1F71"
      >
        VISA
      </text>
    </svg>
  );
}

export function MastercardLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 24" className={className} aria-hidden>
      <circle cx="15" cy="12" r="10" fill="#EB001B" />
      <circle cx="25" cy="12" r="10" fill="#F79E1B" />
      <path
        d="M20 4.5a9.96 9.96 0 0 0 0 15 9.96 9.96 0 0 0 0-15z"
        fill="#FF5F00"
      />
    </svg>
  );
}

export function ApplePayLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 56 22" className={className} aria-hidden>
      <path
        d="M11.6 5.5c.55-.7.92-1.65.83-2.6-.84.05-1.84.55-2.42 1.25-.51.6-.97 1.6-.83 2.5.93.07 1.87-.48 2.42-1.15zM12.8 7.1c-1.34-.08-2.46.76-3.1.76-.65 0-1.62-.71-2.7-.7-1.38.02-2.66.8-3.36 2.04-1.45 2.5-.37 6.2 1.03 8.23.69 1 1.5 2.13 2.58 2.1 1.03-.05 1.43-.67 2.68-.67s1.6.67 2.7.65c1.11-.02 1.82-1.01 2.5-2.02.78-1.15 1.1-2.27 1.12-2.32-.02-.01-2.14-.82-2.16-3.28-.02-2.06 1.69-3.05 1.76-3.1-.98-1.43-2.5-1.59-3.05-1.62z"
        fill="currentColor"
      />
      <text
        x="20"
        y="16"
        fontFamily="-apple-system, system-ui, sans-serif"
        fontSize="14"
        fontWeight="600"
        fill="currentColor"
      >
        Pay
      </text>
    </svg>
  );
}

export function GooglePayLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 70 22" className={className} aria-hidden>
      <g transform="translate(0, 3)">
        <path
          d="M16 8c0 4.42-3.03 7.56-7.5 7.56C4.36 15.56 1 12.2 1 8s3.36-7.56 7.5-7.56c2.03 0 3.72.74 5.03 1.97L11.42 4.55a4.04 4.04 0 0 0-2.92-1.13c-2.5 0-4.52 2.07-4.52 4.58s2.02 4.58 4.52 4.58c2.89 0 3.97-2.07 4.14-3.14H8.5V6.86h7.36c.07.39.14.79.14 1.14z"
          fill="#4285F4"
        />
      </g>
      <text
        x="20"
        y="16"
        fontFamily="-apple-system, system-ui, sans-serif"
        fontSize="14"
        fontWeight="600"
        fill="#5F6368"
      >
        Pay
      </text>
    </svg>
  );
}

// Mobile needs a 44px touch target; the height must live on the link alone so
// the row pitch stays exactly 44px. Never add whitespace-nowrap here: long
// labels then overflow into the neighbouring footer column.
// Padding instead of a bare min-height, so a label that wraps to two lines on
// a phone ("Complete: Rundum-Begleitung") keeps the same air above and below
// as the one-line links instead of touching its neighbours.
// hyphens + overflow-wrap: a single long word ("Конфиденциальность") was 4px
// wider than its column on a 320px phone (audit 18.10.2026).
const linkClass =
  "flex min-h-[44px] items-center py-2 leading-snug text-cream-50/70 transition-colors hover:text-coral-300 hyphens-auto [overflow-wrap:anywhere] sm:inline sm:min-h-0 sm:py-0 sm:leading-normal";
const headingClass =
  "text-[11px] font-semibold uppercase tracking-[0.16em] text-coral-300/90";
// No extra vertical spacing on mobile so the row pitch equals the link height.
const listClass = "mt-1 text-[14px] sm:mt-5 sm:space-y-2.5 sm:text-[13.5px]";
// h-11 w-11 keeps the 44px touch target while the icon stays visually 28px.
const socialClass =
  "inline-flex h-11 w-11 items-center justify-center -m-1.5 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 rounded-md";

export function Footer() {
  const year = new Date().getFullYear();
  const { t, locale } = useTranslation();
  const copyright = t("footer", "copyright").replace("{year}", String(year));
  const footerPath = useSitePathname();
  const noDock = isDockSuppressed(footerPath);
  // On a partner page (/p/lena) the chat and the check keep the partner code.
  const partnerCode = useActivePartnerCode();

  return (
    <footer
      id="kontakt"
      className="relative overflow-hidden border-t border-coral-200/60 bg-navy-900 text-cream-50/85"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-coral-400/40 to-transparent"
      />

      {/* Bottom padding reserves space for the ~82px floating ConversionDock,
          but only on pages that have it; elsewhere (forms, contact, portal)
          the reserve was an empty dark strip at the end of the page. */}
      <div
        className={cn(
          "relative mx-auto max-w-7xl px-5 pt-14 sm:px-8 sm:pb-20 sm:pt-24 lg:px-10",
          noDock
            ? "pb-[calc(3rem+env(safe-area-inset-bottom,0px))]"
            : "pb-[calc(88px+env(safe-area-inset-bottom,0px))]",
        )}
      >
        {/* Everything on one level, as far as the width allows: from sm the
            four link columns share a row, from lg contact joins them, from xl
            the brand as well. The brand column is never narrower than the logo
            (15.5rem) and every heading is a single line, so all lists start at
            the same height. */}
        <div className="grid gap-x-10 gap-y-10 sm:gap-y-14 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-x-12 xl:grid-cols-[15.5rem_minmax(0,1fr)_auto] xl:gap-x-10">
          <div className="lg:col-span-2 xl:col-span-1">
            <Link
              href="/"
              prefetch
              className="inline-flex transition-opacity hover:opacity-90"
            >
              <BrandLogo variant="light" />
            </Link>
            <p className="mt-5 max-w-md text-pretty text-[14px] leading-[1.7] text-cream-50/65 sm:mt-7">
              {t("footer", "tagline")}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3 sm:mt-8">
              <a
                href={SITE.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className={socialClass}
              >
                <InstagramBrandIcon className="h-7 w-7" />
              </a>
              {/* Facebook/TikTok only render once SITE.social holds a URL, so
                  no dead links are shown for profiles that do not exist yet. */}
              {SITE.social.facebook && (
                <a
                  href={SITE.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className={socialClass}
                >
                  <FacebookBrandIcon className="h-7 w-7" />
                </a>
              )}
              {SITE.social.tiktok && (
                <a
                  href={SITE.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className={socialClass}
                >
                  <TiktokBrandIcon className="h-7 w-7" />
                </a>
              )}
              {SITE.social.pinterest && (
                <a
                  href={SITE.social.pinterest}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Pinterest"
                  className={socialClass}
                >
                  <PinterestBrandIcon className="h-7 w-7" />
                </a>
              )}
              <a
                href={whatsappLink(undefined, locale, partnerCode)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className={socialClass}
              >
                <WhatsAppBrandIcon className="h-7 w-7" />
              </a>
              <a
                href={telegramLink()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
                className={socialClass}
              >
                <TelegramBrandIcon className="h-7 w-7" />
              </a>
            </div>
          </div>

          {/* max-sm:order-* pairs columns of equal length on mobile (5+5, then
              3+3) so no gap opens under the shorter one. */}
          <nav
            aria-label={t("footer", "navLabel")}
            className="grid grid-cols-2 items-start gap-x-8 gap-y-10 sm:grid-cols-4 sm:gap-x-6"
          >
            <div className="max-sm:order-1">
              <div className={headingClass}>{t("footer", "services")}</div>
              <ul className={listClass}>
                <li><Link href="/preise" className={linkClass}>{t("footer", "svcComplete")}</Link></li>
                <li><Link href="/preise" className={linkClass}>{t("footer", "svcEssential")}</Link></li>
                <li><Link href="/gemeindewechsel" className={linkClass}>{t("pricing", "pkgSwitchName")}</Link></li>
                <li><Link href="/trautermine-daenemark" className={linkClass}>{t("footer", "svcDates")}</Link></li>
                <li><Link href="/preise" className={linkClass}>{t("footer", "svcApostille")}</Link></li>
                <li><Link href={bookingLink()} className={linkClass}>{t("footer", "svcBeratung")}</Link></li>
                <li><Link href={partnerHrefs(partnerCode).check} className={linkClass}>{t("common", "navCheck")}</Link></li>
                <li><Link href="/heiraten-daenemark-herkunftsland" className={linkClass}>{t("footer", "svcCountryRules")}</Link></li>
                <li><Link href="/anfrage" className={linkClass}>{t("footer", "svcInquiry")}</Link></li>
              </ul>
            </div>

            <div className="max-sm:order-3">
              <div className={headingClass}>{t("footer", "locations")}</div>
              <ul className={listClass}>
                <li><Link href="/heiraten-daenemark-kopenhagen" className={linkClass}>{t("footer", "locationCopenhagen")}</Link></li>
                <li><Link href="/heiraten-daenemark-toender" className={linkClass}>{t("footer", "locationToender")}</Link></li>
                <li><Link href="/heiraten-daenemark-aeroe" className={linkClass}>{t("footer", "locationAeroe")}</Link></li>
                <li><Link href="/ratgeber/heiraten-in-aabenraa" className={linkClass}>{t("footer", "locationAabenraa")}</Link></li>
                <li><Link href="/ratgeber/heiraten-in-soenderborg" className={linkClass}>{t("footer", "locationSoenderborg")}</Link></li>
                <li><Link href="/ratgeber/heiraten-in-haderslev" className={linkClass}>{t("footer", "locationHaderslev")}</Link></li>
              </ul>
              <p className="mt-3 text-[11.5px] leading-relaxed text-cream-50/55 sm:mt-4">
                {t("footer", "locationsHint")}
              </p>
            </div>

            <div className="max-sm:order-4">
              <div className={headingClass}>{t("footer", "company")}</div>
              <ul className={listClass}>
                <li><Link href="/ueber-uns" prefetch className={linkClass}>{t("common", "navAbout")}</Link></li>
                <li><Link href="/fallbeispiele" className={linkClass}>{t("footer", "casesLink")}</Link></li>
                <li><Link href="/ratgeber" className={linkClass}>{t("common", "navRatgeber")}</Link></li>
                <li><Link href="/#faq" className={linkClass}>FAQ</Link></li>
                <li><Link href="/partner" className={linkClass}>{t("footer", "partnerProgram")}</Link></li>
              </ul>
            </div>

            <div className="max-sm:order-2">
              <div className={headingClass}>{t("footer", "legal")}</div>
              <ul className={listClass}>
                <li><Link href="/impressum" className={linkClass}>{t("footer", "legalImpressum")}</Link></li>
                <li><Link href="/datenschutz" className={linkClass}>{t("footer", "legalDatenschutz")}</Link></li>
                <li><Link href="/agb" className={linkClass}>{t("footer", "legalAGB")}</Link></li>
                <li><Link href="/widerruf" className={linkClass}>{t("footer", "legalWiderruf")}</Link></li>
                <li><Link href="/vertrag-widerrufen" className={linkClass}>{t("footer", "legalWithdrawNow")}</Link></li>
                <li><Link href="/vollmacht" className={linkClass}>{t("footer", "legalVollmacht")}</Link></li>
                <li><button type="button" onClick={reopenConsent} className={cn(linkClass, "text-start")}>{t("footer", "legalCookies")}</button></li>
              </ul>
            </div>
          </nav>

          <div className="max-lg:border-t max-lg:border-cream-50/10 max-lg:pt-8">
            <div className={headingClass}>{t("footer", "contact")}</div>
            <ul className="mt-1 text-[14px] sm:mt-5 sm:space-y-3 sm:text-[13.5px]">
              <li>
                <Link
                  href="/kontakt"
                  className="group flex min-h-[44px] items-center gap-2.5 font-semibold text-cream-50/90 transition-colors hover:text-coral-300 sm:min-h-0"
                >
                  <ArrowRight
                    className="h-4 w-4 shrink-0 text-coral-300 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span>{t("footer", "contactPageLabel")}</span>
                </Link>
              </li>
              <li>
                <a
                  href={whatsappLink(undefined, locale, partnerCode)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex min-h-[44px] items-center gap-2.5 text-cream-50/80 transition-colors hover:text-coral-300 sm:min-h-0"
                >
                  <MessageCircle
                    className="h-4 w-4 shrink-0 text-coral-300 transition-colors group-hover:text-coral-200"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span dir="ltr" className="whitespace-nowrap">{SITE.phone}</span>
                  <span className="text-[11px] uppercase tracking-[0.08em] text-cream-50/70">
                    WhatsApp
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={mailLink(t("footer", "mailSubject"))}
                  className="group flex min-h-[44px] items-center gap-2.5 text-cream-50/80 transition-colors hover:text-coral-300 sm:min-h-0"
                >
                  <Mail
                    className="h-4 w-4 shrink-0 text-coral-300 transition-colors group-hover:text-coral-200"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span className="break-all sm:break-normal">{SITE.email}</span>
                </a>
              </li>
              <li>
                <Link
                  href={bookingLink()}
                  data-track="footer_book_call"
                  className="group flex min-h-[44px] items-center gap-2.5 text-cream-50/80 transition-colors hover:text-coral-300 sm:min-h-0"
                >
                  <CalendarClock
                    className="h-4 w-4 shrink-0 text-coral-300 transition-colors group-hover:text-coral-200"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span>{t("footer", "contactBookingLabel")}</span>
                </Link>
              </li>
            </ul>
            <p className="mt-4 text-[12px] leading-relaxed text-cream-50/55">
              {t("footer", "hoursLine")}
              <br />
              {t("footer", "replyWithin24h")}
            </p>
          </div>
        </div>

        <div className="mt-10 border-t border-cream-50/10 pt-6 text-[12px] text-cream-50/70 sm:mt-16">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex items-center gap-2 text-cream-50/70">
              <Lock className="h-3.5 w-3.5 text-cream-50/60" strokeWidth={1.8} aria-hidden />
              <span>{t("footer", "securityLine")}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("footer", "paymentLine")}>
              <PaymentChip label="Visa">
                <VisaLogo className="h-[14px] w-auto" />
              </PaymentChip>
              <PaymentChip label="Mastercard">
                <MastercardLogo className="h-[18px] w-auto" />
              </PaymentChip>
              <PaymentChip label="Apple Pay">
                <ApplePayLogo className="h-[15px] w-auto text-ink-900" />
              </PaymentChip>
              <PaymentChip label="Google Pay">
                <GooglePayLogo className="h-[15px] w-auto" />
              </PaymentChip>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>{copyright}</div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="whitespace-nowrap font-medium tracking-[0.04em] text-cream-50/65">
                {t("footer", "regionLine")}
              </span>
              <span aria-hidden className="hidden text-cream-50/25 sm:inline">·</span>
              <span
                role="img"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-cream-50/60"
                aria-label={t("footer", "madeWithAria")}
              >
                <span aria-hidden>{t("footer", "madeWithPrefix")}</span>
                <Heart
                  className="h-3.5 w-3.5 fill-coral-400/80 text-coral-300"
                  strokeWidth={1.8}
                  aria-hidden
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Light chip wrapper that keeps payment logos legible in their brand colours. */
export function PaymentChip({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  /** Border override; the default suits the dark footer, light surfaces pass e.g. border-navy-100. */
  className?: string;
}) {
  return (
    <span
      role="img"
      title={label}
      aria-label={label}
      className={`inline-flex h-8 items-center justify-center rounded-md border bg-cream-50 px-2.5 ${className ?? "border-cream-50/15"}`}
    >
      {children}
    </span>
  );
}
